import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRightIcon, PlayIcon } from '@heroicons/react/24/outline';
import { trackEvent } from '../../utils/analytics';

interface HeroProps {
  onCtaVisibilityChange?: (isVisible: boolean) => void;
}

const Hero: React.FC<HeroProps> = ({ onCtaVisibilityChange }) => {
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ctaRef.current || !onCtaVisibilityChange) return;

    const observer = new IntersectionObserver(
      ([entry]) => onCtaVisibilityChange(entry.isIntersecting),
      { threshold: 0 }
    );

    observer.observe(ctaRef.current);
    return () => observer.disconnect();
  }, [onCtaVisibilityChange]);

  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.5], [0, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);

  return (
    <section className="relative min-h-screen px-6 pt-24 pb-24 overflow-hidden flex items-center justify-center">
      <motion.div
        style={{ y: heroY, opacity }}
        className="relative z-10 max-w-4xl mx-auto text-center"
      >
        {/* Headline */}
        <div className="overflow-hidden">
          <motion.h1
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-8 leading-[0.95] tracking-tight"
            style={{ perspective: '1000px' }}
          >
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="block relative"
            >
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                }}
              >
                Learning that builds forward.
              </span>
            </motion.span>
          </motion.h1>
        </div>

        {/* Subheadline - Problem + Solution */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-12 max-w-2xl mx-auto"
        >
          <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed md:whitespace-nowrap">
            Vidya understands what you’ve truly learned. Understanding compounds from there.
          </p>
        </motion.div>

        {/* CTA buttons */}
        <motion.div
          ref={ctaRef}
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 1, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-3"
        >
          <Link to="/signup" onClick={() => trackEvent('cta_click', { cta_text: 'try_vidya_free', cta_location: 'hero' })}>
            <motion.span
              whileHover={{
                scale: 1.05,
                boxShadow: '0 0 80px rgba(6, 182, 212, 0.6), 0 0 120px rgba(6, 182, 212, 0.3)',
              }}
              whileTap={{ scale: 0.95 }}
              className="group relative inline-flex items-center gap-3 px-8 py-4 rounded-full font-semibold text-white transition-all cursor-pointer overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                boxShadow: '0 0 50px rgba(6, 182, 212, 0.4), 0 4px 24px rgba(0, 0, 0, 0.15)',
              }}
            >
              <motion.span
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full"
                whileHover={{ translateX: '100%' }}
                transition={{ duration: 0.6 }}
              />
              <span className="relative">Try Vidya Free</span>
              <motion.span
                className="relative"
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
              >
                <ArrowRightIcon className="w-5 h-5" />
              </motion.span>
            </motion.span>
          </Link>

          <a href="#demo" onClick={() => trackEvent('cta_click', { cta_text: 'see_how_it_works', cta_location: 'hero' })}>
            <motion.span
              whileHover={{
                scale: 1.05,
                backgroundColor: 'rgba(6, 182, 212, 0.1)',
                borderColor: 'rgba(6, 182, 212, 0.5)',
              }}
              whileTap={{ scale: 0.95 }}
              className="group inline-flex items-center gap-2.5 px-8 py-4 rounded-full font-semibold transition-all cursor-pointer text-gray-800 border border-gray-300"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <motion.span
                className="w-8 h-8 rounded-full flex items-center justify-center group-hover:bg-cyan-500/20 transition-colors bg-gray-200/50"
                whileHover={{ scale: 1.1 }}
              >
                <PlayIcon className="w-4 h-4 group-hover:text-cyan-500 transition-colors ml-0.5 text-gray-600" />
              </motion.span>
              See how it works
            </motion.span>
          </a>
        </motion.div>

      </motion.div>

      {/* Explore indicator pinned to bottom of hero (creates separation before Story) */}
      <motion.a
        href="#testimonials"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 hover:text-cyan-500 transition-colors cursor-pointer group text-gray-500"
      >
        <motion.span
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-xs font-medium tracking-widest uppercase"
        >
          Explore
        </motion.span>
        <motion.div className="w-6 h-10 rounded-full border-2 group-hover:border-cyan-500/50 flex justify-center pt-2 transition-colors border-gray-400">
          <motion.div
            animate={{ y: [0, 12, 0], opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
            className="w-1.5 h-1.5 rounded-full group-hover:bg-cyan-500 transition-colors bg-gray-400"
          />
        </motion.div>
      </motion.a>
    </section>
  );
};

export default Hero;
